import React, { useState, useEffect, useCallback, memo } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { settingsManager } from '@/lib/vatUtils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { 
  Accessibility, 
  X, 
  Type, 
  Eye,
  Zap,
  MousePointer2,
  BookOpen,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  Contrast,
  Volume2
} from 'lucide-react';

// SettingRow component - defined outside to prevent re-creation during render
const SettingRow = memo(({ icon: Icon, label, description, children }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-border/50 last:border-0">
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" aria-hidden="true" />
      <div>
        <Label className="font-medium">{label}</Label>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
    </div>
    {children}
  </div>
));

SettingRow.displayName = 'SettingRow';

/**
 * Floating Accessibility Widget - WCAG 2.1 AA/AAA Compliant
 * Auto-detects system preferences and provides full customization
 */
const AccessibilityWidget = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Accessibility settings with auto-detection
  const [settings, setSettings] = useState(() => {
    const saved = settingsManager.get();
    const defaults = {
      fontSize: 100,
      reducedMotion: false,
      highContrast: false,
      enhancedFocus: false,
      dyslexiaFont: false,
      largeText: false,
      invertColors: false,
      saturation: 100,
      readingGuide: false,
      textToSpeech: false,
    };
    return { ...defaults, ...saved.accessibility };
  });

  // Apply settings to DOM
  const applySettings = useCallback((s) => {
    const root = document.documentElement;
    
    // Font size
    root.style.setProperty('--a11y-font-scale', s.fontSize / 100);
    root.style.fontSize = `${s.fontSize}%`;
    
    // Classes
    const classes = {
      'reduce-motion': s.reducedMotion,
      'high-contrast': s.highContrast,
      'enhanced-focus': s.enhancedFocus,
      'dyslexia-font': s.dyslexiaFont,
      'large-text': s.largeText,
      'invert-colors': s.invertColors,
      'reading-guide-active': s.readingGuide,
    };

    Object.entries(classes).forEach(([className, enabled]) => {
      if (enabled) {
        root.classList.add(className);
      } else {
        root.classList.remove(className);
      }
    });

    // Saturation
    if (s.saturation !== 100) {
      root.style.filter = `saturate(${s.saturation}%)`;
    } else {
      root.style.filter = '';
    }
  }, []);

  // Auto-detect system accessibility preferences
  const autoDetectPreferences = useCallback(() => {
    setSettings(currentSettings => {
      const newSettings = { ...currentSettings };
      let changed = false;

      // Detect prefers-reduced-motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        if (!currentSettings.reducedMotion) {
          newSettings.reducedMotion = true;
          changed = true;
        }
      }

      // Detect prefers-contrast
      if (window.matchMedia('(prefers-contrast: more)').matches) {
        if (!currentSettings.highContrast) {
          newSettings.highContrast = true;
          changed = true;
        }
      }

      if (changed) {
        applySettings(newSettings);
        settingsManager.set({ accessibility: newSettings });
        return newSettings;
      }
      return currentSettings;
    });
  }, [applySettings]);

  // Auto-detect system preferences on mount
  useEffect(() => {
    autoDetectPreferences();
  }, [autoDetectPreferences]);

  // Update single setting
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      applySettings(newSettings);
      settingsManager.set({ accessibility: newSettings });
      return newSettings;
    });
  }, [applySettings]);

  // Listen to system preference changes
  useEffect(() => {
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const contrastQuery = window.matchMedia('(prefers-contrast: more)');

    const handleReducedMotionChange = (e) => {
      if (e.matches) {
        updateSetting('reducedMotion', true);
      }
    };

    const handleContrastChange = (e) => {
      if (e.matches) {
        updateSetting('highContrast', true);
      }
    };

    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
    contrastQuery.addEventListener('change', handleContrastChange);

    return () => {
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
      contrastQuery.removeEventListener('change', handleContrastChange);
    };
  }, [updateSetting]);

  // Reset all settings
  const resetSettings = useCallback(() => {
    const defaults = {
      fontSize: 100,
      reducedMotion: false,
      highContrast: false,
      enhancedFocus: false,
      dyslexiaFont: false,
      largeText: false,
      invertColors: false,
      saturation: 100,
      readingGuide: false,
      textToSpeech: false,
    };
    setSettings(defaults);
    applySettings(defaults);
    settingsManager.set({ accessibility: defaults });
  }, [applySettings]);

  // Apply on mount - use initialSettings ref to avoid reapplying on every settings change
  useEffect(() => {
    // Apply current settings once on mount
    applySettings(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applySettings]);

  // Keyboard shortcut to open widget (Alt + A)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === 'a') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reading guide effect
  useEffect(() => {
    if (!settings.readingGuide) return;

    const guide = document.createElement('div');
    guide.id = 'reading-guide';
    guide.style.cssText = `
      position: fixed;
      left: 0;
      right: 0;
      height: 40px;
      background: rgba(255, 255, 0, 0.2);
      border-top: 2px solid rgba(255, 200, 0, 0.5);
      border-bottom: 2px solid rgba(255, 200, 0, 0.5);
      pointer-events: none;
      z-index: 9998;
      transition: top 0.1s ease;
    `;
    document.body.appendChild(guide);

    const moveGuide = (e) => {
      guide.style.top = `${e.clientY - 20}px`;
    };

    document.addEventListener('mousemove', moveGuide);

    return () => {
      document.removeEventListener('mousemove', moveGuide);
      guide.remove();
    };
  }, [settings.readingGuide]);

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          fixed bottom-6 right-6 z-[60]
          w-14 h-14 rounded-full
          bg-primary text-primary-foreground
          shadow-lg hover:shadow-xl
          flex items-center justify-center
          transition-all duration-300
          hover:scale-110
          focus:outline-none focus:ring-4 focus:ring-primary/50
        `}
        aria-label={t('accessibilitySettings') || 'Accessibility Settings'}
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
        data-testid="a11y-widget-button"
      >
        <Accessibility className="w-7 h-7" aria-hidden="true" />
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div
          id="accessibility-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="a11y-panel-title"
          className={`
            fixed bottom-24 right-6 z-[60]
            w-[360px] max-h-[65vh]
            bg-background border border-border
            rounded-2xl shadow-2xl
            overflow-hidden
            animate-in slide-in-from-bottom-5 fade-in duration-300
          `}
          data-testid="a11y-widget-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-primary/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Accessibility className="w-5 h-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h2 id="a11y-panel-title" className="font-semibold">
                  {t('accessibilitySettings') || 'Accessibility'}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {t('accessibilitySettingsDesc') || 'Customize for your needs'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMinimized(!isMinimized)}
                aria-label={isMinimized ? 'Expand' : 'Minimize'}
                className="h-8 w-8"
              >
                {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                aria-label="Close accessibility panel"
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          {!isMinimized && (
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {/* Quick Profiles */}
              <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs font-medium text-muted-foreground mb-2 uppercase">
                  {t('quickProfiles') || 'Quick Profiles'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      updateSetting('highContrast', true);
                      updateSetting('enhancedFocus', true);
                      updateSetting('fontSize', 125);
                    }}
                    className="text-xs"
                  >
                    👁️ {t('visionImpaired') || 'Vision'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      updateSetting('reducedMotion', true);
                      updateSetting('saturation', 50);
                    }}
                    className="text-xs"
                  >
                    🧠 {t('cognitive') || 'Cognitive'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      updateSetting('dyslexiaFont', true);
                      updateSetting('fontSize', 110);
                    }}
                    className="text-xs"
                  >
                    📖 {t('dyslexia') || 'Dyslexia'}
                  </Button>
                </div>
              </div>

              {/* Font Size */}
              <SettingRow
                icon={Type}
                label={t('fontSize') || 'Font Size'}
                description={`${settings.fontSize}%`}
              >
                <div className="w-32">
                  <Slider
                    min={75}
                    max={200}
                    step={5}
                    value={[settings.fontSize]}
                    onValueChange={([value]) => updateSetting('fontSize', value)}
                    aria-label="Font size"
                  />
                </div>
              </SettingRow>

              {/* High Contrast */}
              <SettingRow
                icon={Contrast}
                label={t('highContrast') || 'High Contrast'}
                description={t('highContrastDesc') || 'Increase visibility'}
              >
                <Switch
                  checked={settings.highContrast}
                  onCheckedChange={(checked) => updateSetting('highContrast', checked)}
                  aria-label="High contrast mode"
                />
              </SettingRow>

              {/* Enhanced Focus */}
              <SettingRow
                icon={MousePointer2}
                label={t('enhancedFocus') || 'Enhanced Focus'}
                description={t('enhancedFocusDesc') || 'Larger focus indicators'}
              >
                <Switch
                  checked={settings.enhancedFocus}
                  onCheckedChange={(checked) => updateSetting('enhancedFocus', checked)}
                  aria-label="Enhanced focus indicators"
                />
              </SettingRow>

              {/* Reduced Motion */}
              <SettingRow
                icon={Zap}
                label={t('reducedMotion') || 'Reduced Motion'}
                description={t('reducedMotionDesc') || 'Stop animations'}
              >
                <Switch
                  checked={settings.reducedMotion}
                  onCheckedChange={(checked) => updateSetting('reducedMotion', checked)}
                  aria-label="Reduced motion"
                />
              </SettingRow>

              {/* Dyslexia Font */}
              <SettingRow
                icon={BookOpen}
                label={t('dyslexiaFont') || 'Dyslexia Font'}
                description={t('dyslexiaFontDesc') || 'OpenDyslexic font'}
              >
                <Switch
                  checked={settings.dyslexiaFont}
                  onCheckedChange={(checked) => updateSetting('dyslexiaFont', checked)}
                  aria-label="Dyslexia friendly font"
                />
              </SettingRow>

              {/* Reading Guide */}
              <SettingRow
                icon={Eye}
                label={t('readingGuide') || 'Reading Guide'}
                description={t('readingGuideDesc') || 'Highlight line'}
              >
                <Switch
                  checked={settings.readingGuide}
                  onCheckedChange={(checked) => updateSetting('readingGuide', checked)}
                  aria-label="Reading guide"
                />
              </SettingRow>

              {/* Saturation */}
              <SettingRow
                icon={Eye}
                label={t('saturation') || 'Color Saturation'}
                description={`${settings.saturation}%`}
              >
                <div className="w-32">
                  <Slider
                    min={0}
                    max={100}
                    step={10}
                    value={[settings.saturation]}
                    onValueChange={([value]) => updateSetting('saturation', value)}
                    aria-label="Color saturation"
                  />
                </div>
              </SettingRow>

              {/* Reset Button */}
              <Button 
                variant="outline" 
                className="w-full mt-4"
                onClick={resetSettings}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t('resetToDefault') || 'Reset to Default'}
              </Button>

              {/* Keyboard shortcut hint */}
              <p className="text-xs text-center text-muted-foreground mt-3">
                {t('shortcutHint') || 'Press'} <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Alt + A</kbd> {t('toToggle') || 'to toggle'}
              </p>
            </div>
          )}

          {/* Auto-detected badge */}
          <div className="px-4 py-2 bg-muted/30 border-t text-xs text-muted-foreground flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            {t('autoDetected') || 'Auto-detects system preferences'}
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
